import React, { useEffect } from "react";
import { Autocomplete } from "@/compat/Autocomplete";
import { allSitesOption } from "../constants/siteSelectConstants";
import { Business } from "@mui/icons-material";
import { TextField } from "@mui/material";
import { makeStyles } from "@/compat/muiStyles";
import { useAppSelector, useAppDispatch } from "@/redux/hooks";
import useAccountAppSites from "../hooks/queries/useAccountAppSites";
import { selectSite, setSites } from "../redux/actions/accountActions";
import useIsMobile from "../hooks/useIsMobile";

const useStyles = makeStyles((theme) => ({
    siteSelectIcon: {
        marginRight: theme.spacing(1),
    },
}));

interface SiteSelectProps {
    size?: string;
    variant?: string;
    className?: string;
}

const SiteSelect = ({ size = "medium", variant = "outlined", className }: SiteSelectProps) => {
    const classes = useStyles();
    const dispatch = useAppDispatch();

    const { selectedAccount, selectedSite, activeAppId } = useAppSelector(
        (state) => state.account
    );
    const { data: accountSitesData } = useAccountAppSites({
        childExternalIds: [
            ...(selectedAccount?.childExternalIds || []),
            selectedAccount?.account?.externalId,
        ],
        activeAppId,
    });

    const isMobile = useIsMobile();

    const handleSiteChange = (event, newValue) => {
        dispatch(
            selectSite(newValue.name === allSitesOption.name ? null : newValue)
        );
    };

    useEffect(() => {
        if (!accountSitesData || !activeAppId) return;
        dispatch(setSites({ sites: accountSitesData, appId: activeAppId }));
    }, [dispatch, accountSitesData, activeAppId]);

    if (!accountSitesData?.length)
        return null;

    return (
        <Autocomplete
            size={size}
            fullWidth
            blurOnSelect
            className={className}
            value={selectedSite || allSitesOption}
            onChange={handleSiteChange}
            getOptionLabel={(option) => option.name}
            disableClearable
            renderOption={(option) => (
                <>
                    <Business className={classes.siteSelectIcon} />
                    {option.name}
                </>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label="Select a site"
                    variant={variant}
                    inputProps={{
                        ...params.inputProps,
                        autoComplete: "new-password", // disable autocomplete and autofill
                    }}
                    SelectProps={{
                        native: isMobile,
                    }}
                />
            )}
            options={[allSitesOption, ...accountSitesData.sort((a, b) => a.name < b.name ? -1 : 1)]}
        />
    );
};

export default SiteSelect;